import { Router, ScreenBase } from "@frui.ts/screens";
import { action, computed, observable, reaction } from "mobx";
import { bound } from "@frui.ts/helpers";
import AppRouter from "services/appRouter";
import LocalizationService from "services/localizationService";
import FuelTypeViewModel from "./fuelTypeViewModel";
import ContactViewModel from "./contactViewModel";
import ChargeTypeSelectViewModel from "viewModels/common/chargeTypeSelectViewModel";
import { interfaces } from "inversify";
import ValiditySelectViewModel from "../components/validitySelectViewModel";
import EnumService from "services/enumService";
import { attachAutomaticBlurValidator, setVisitedProperty } from "utils/validationPatch";
import {
  maxValidSince,
  zonedCZTimeToUtc,
  utcToCZZonedTime,
  dateToIsoDate,
  isSameOrBeforePragueDay,
  dateStringWithServerZone,
  dateToIsoString,
} from "utils/dateUtils";
import ChargeValidityRuleResponse from "entities/chargeValidityRuleResponse";
import ChargeOrderChargeItem, { ChargeTypeIdValidationRules } from "models/chargeOrderChargeItem";
import ChargeOrderService from "services/chargeOrderService";
import { validate, ManualEntityValidator, validateAll } from "@frui.ts/validation";
import { scrollToElement, scrollToFirstError } from "utils/scrollTo";
import debounce from "lodash/debounce";
import { onlyResolvesLast } from "awesome-only-resolves-last-promise/dist";
import ChargeValidityInstructionsResponse from "entities/chargeValidityInstructionsResponse";
import { repeatIfFailed } from "services/requestRecoveryService";
import VehicleRepository from "repositories/vehicleRepository";
import { format, isBefore, startOfDay } from "date-fns";
import ChargeValidity from "entities/chargeValidity";
import ChargeTypesService from "services/chargeTypesService";
import { startOfCzechDay, endOfCzechDay } from "../../../../utils/dateUtils";
import ChargeOverlapType from "entities/chargeOverlapType";
import ChargeOverlapResponse from "entities/chargeOverlapResponse";
import TimeService from "services/timeService";
import EventBus, {
  chargeValidationFailed,
  currentServerTime,
  preselectChargeType,
  proposedPurchageCharge,
} from "services/eventBus";
export const ChargeTypeViewNavigationName = "charge";

@Router.registerRoute({
  name: Router.Self,
  route: ChargeTypeViewNavigationName,
})
export default class ChargeTypeViewModel extends ScreenBase {
  public chargeSelectViewModel: ChargeTypeSelectViewModel;
  public validSinceSelectViewModel: ValiditySelectViewModel;

  private unsubscriptions: Array<() => void> = [];
  private debouncedCheckProposedValidity = debounce(this.checkProposedValidity, 400);
  private lastResolveGetProposedValidity = onlyResolvesLast(this.getProposedValidity);
  protected manualValidator = new ManualEntityValidator<ChargeOrderChargeItem>(false); // Použijeme setVisitedProperty pro zobrazení errorů

  @observable checkingProposedValidity = false;
  @observable formElement?: HTMLFormElement;
  @observable validSinceDaysBefore?: Date;
  @observable validSince?: Date;
  @observable validSinceString?: string;
  @observable isBeforePosibble?: boolean;
  @observable isSingleCharTypeToday?: boolean;
  @observable showExemptionOverlap?: boolean;
  @observable showChargeOverlap?: boolean;
  /** Flag to determine whether to show the orange warning banner. */
  @observable proposedValidityFlag?: boolean;
  /** Temporarily determines whether a proposed validity exists from the BE. */
  @observable proposedValidityExist: boolean;
  /** The validity change by the BE can only happen once. If true, validity cannot be changed anymore by the system. */
  @observable proposedValiditySetAlready = false;
  @observable overlappingCharges?: ChargeOverlapResponse[];
  @observable overlapTypes?: ChargeOverlapType[];
  @observable isServerTimeSame?: boolean;
  @observable currentServerTime: Date;
  @observable updatedProposed = false;

  constructor(
    public chargeOrderChargeItem: ChargeOrderChargeItem,
    public localizationService: LocalizationService,
    private chargeOrderService: ChargeOrderService,
    private chargeTypesService: ChargeTypesService,
    public timeService: TimeService,
    public enumService: EnumService,
    private router: AppRouter,
    private vehicleRepository: VehicleRepository,
    private chargeSelectViewModelFactory: ReturnType<typeof ChargeTypeSelectViewModel.Factory>,
    private validSinceSelectVMFactory: ReturnType<typeof ValiditySelectViewModel.Factory>,
    private eventBus: EventBus
  ) {
    super();
    this.navigationName = ChargeTypeViewNavigationName;
    this.nameValue = "eshop.title";
    this.chargeSelectViewModel = this.chargeSelectViewModelFactory(
      this.chargeOrderChargeItem,
      this.chargeOrderService.chargeOrderItem.id
    );
    this.validSinceSelectViewModel = this.validSinceSelectVMFactory(this.chargeOrderChargeItem);
    this.validSinceSelectViewModel.getPostponedValidityMaxDays(
      this.timeService.currentServerTime ?? new Date(),
      this.enumService.values("charge_validity_rules") as ChargeValidityRuleResponse[]
    );
    this.unsubscriptions.push(
      this.eventBus.subscribe(preselectChargeType, this.checkPriceListItem),
      this.eventBus.subscribe(currentServerTime, () => {
        this.serverTime;
      })
    );

    // Základní validátor bez date range (nezávislý na server time)
    attachAutomaticBlurValidator(this.chargeOrderChargeItem, {
      ...ChargeTypeIdValidationRules,
      validSince: {
        required: true,
        manualValidation: this.manualValidator,
      },
    });
  }

  protected async onInitialize(): Promise<Promise<any> | void> {
    this.proposedValidityExist = false;
    await this.timeService.getCurrentServerTime();
    this.updateDateRangeValidation();

    this.unsubscriptions.push(
      reaction(
        () => [this.chargeOrderChargeItem.validSince, this.chargeOrderChargeItem.chargeTypeId],
        () => {
          this.proposedValidityReaction();
        }
      ),
      reaction(
        () => this.chargeOrderChargeItem.validSince,
        () => {
          this.proposedValidityExistReaction();
        }
      ),
      reaction(
        () => [this.chargeOrderChargeItem.validSince, this.chargeOrderChargeItem.chargeTypeId],
        () => {
          this.checkWarnings();
        }
      ),
      reaction(
        () => this.chargeOrderChargeItem.validSince,
        () => {
          // Po změně validSince automaticky aktualizujeme date range validaci
          this.updateDateRangeValidation();
        }
      ),
      reaction(
        () => this.chargeTypesService.isInitialized,
        () => {
          this.checkWarnings();
        }
      )
    );
    return super.onInitialize();
  }

  protected async onActivate() {
    if (this.stepper) {
      scrollToElement(this.stepper);
    }
    this.updateDateRangeValidation();

    this.checkPriceListItem();
    this.validSinceString = this.chargeOrderChargeItem.validSince
      ? dateToIsoDate(this.chargeOrderChargeItem.validSince)
      : dateToIsoString(new Date());
    this.checkProposedValidity();
    this.checkWarnings();
    this.updateChargeOrderItem();
    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @computed
  get isDisabled() {
    return !!this.chargeOrderChargeItem.chargeTypeId && !!this.chargeOrderChargeItem.validSince;
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @action.bound
  checkPriceListItem(data?: ReturnType<typeof preselectChargeType>) {
    const id = data?.payload.chargeType.id;
    const chargeOrderItem = this.chargeOrderService.chargeOrderItem;
    if (id || chargeOrderItem._chargeTypeId) {
      this.chargeOrderChargeItem.chargeTypeId = id ?? chargeOrderItem?._chargeTypeId;
      return;
    }
    if (chargeOrderItem.priceListItemId) {
      this.chargeOrderChargeItem.chargeTypeId = chargeOrderItem?._priceListItems?.find(
        item => item.id === chargeOrderItem.priceListItemId
      )?.chargeTypeId;
    }
  }

  @action.bound
  checkServerTime(data?: ReturnType<typeof currentServerTime>) {
    this.timeService.currentServerTime = data?.payload.date ?? new Date();
  }

  @action.bound
  private async checkWarnings() {
    const earliestPossibleDate =
      this.chargeOrderService.chargeOrder._earliestPossibleValidSinceDate ??
      this.chargeOrderService.earliestPossibleValidSinceDate;
    this.currentServerTime = this.timeService.currentServerTime ? this.timeService.currentServerTime : new Date();
    if (!this.chargeOrderChargeItem.validSince || !this.chargeTypesService.isInitialized || !earliestPossibleDate) {
      return;
    }
    this.isSingleCharTypeToday =
      dateToIsoDate(this.chargeOrderChargeItem.validSince) === dateToIsoString(this.currentServerTime) &&
      this.chargeTypesService.findChargeType(this.chargeOrderChargeItem.chargeTypeId)?.validFor === ChargeValidity.SingleDay;
    this.isBeforePosibble = isBefore(
      startOfCzechDay(utcToCZZonedTime(this.chargeOrderChargeItem.validSince)),
      startOfCzechDay(utcToCZZonedTime(earliestPossibleDate))
    );
    this.validSinceDaysBefore = dateStringWithServerZone(utcToCZZonedTime(earliestPossibleDate), this.serverTime);

    if (!this.overlapTypes) {
      return;
    }
    if (!this.proposedValiditySetAlready) {
      this.overlapTypes = this.overlapTypes;
      this.showExemptionOverlap = this.overlapTypes.includes(ChargeOverlapType.ExemptionOverlap);
      this.showChargeOverlap = this.overlapTypes.includes(ChargeOverlapType.ChargeOverlap);
      this.overlappingCharges = this.overlappingCharges ? this.overlappingCharges : [];
    }
  }

  @action.bound
  private updateChargeOrderItem() {
    const chargeOrderItem = this.chargeOrderService.chargeOrderItem;
    if (this.chargeOrderChargeItem.chargeTypeId) {
      chargeOrderItem.priceListItemId =
        chargeOrderItem?._priceListItems.find(item => item.chargeTypeId === this.chargeOrderChargeItem.chargeTypeId)?.id ?? "";
    }
    if (this.chargeOrderChargeItem.validSince) {
      chargeOrderItem.validity.hasImmediateValidity =
        this.chargeOrderChargeItem.validSince &&
        dateToIsoDate(this.chargeOrderChargeItem.validSince) <= dateToIsoString(this.currentServerTime);
    }
    if (!this.updatedProposed) {
      if (chargeOrderItem.validity.hasImmediateValidity) {
        chargeOrderItem._hasImmediateValidity = chargeOrderItem.validity.hasImmediateValidity;
        this.chargeOrderChargeItem.validSince = zonedCZTimeToUtc(dateToIsoString(this.serverTime));
        this.updatedProposed = true;
      } else {
        this.chargeOrderChargeItem.validSince = chargeOrderItem.validity.validSinceDate;
        this.updatedProposed = true;
      }
    }
    chargeOrderItem.validity.validSinceDate = this.chargeOrderChargeItem.validSince;
    chargeOrderItem.validity._validUntilDate = this.chargeOrderChargeItem._validUntil;
    chargeOrderItem.validity._proposedValidityExist = this.proposedValidityExist;
    chargeOrderItem._chargeTypeId = this.chargeOrderChargeItem.chargeTypeId;
    chargeOrderItem._singleChargeType =
      this.chargeTypesService.findChargeType(this.chargeOrderChargeItem.chargeTypeId)?.validFor === ChargeValidity.SingleDay;
    this.chargeOrderService.persistChargeOrder();
  }

  @action.bound
  private getProposedValidity(
    countryId: string,
    licensePlate: string,
    priceListItemId: string,
    hasImmediateValidity?: boolean,
    validSinceDate?: Date,
    validSinceDateString?: string
  ) {
    return this.vehicleRepository.proposedValidity(
      countryId,
      licensePlate,
      priceListItemId,
      hasImmediateValidity,
      validSinceDate,
      validSinceDateString
    );
  }

  @action.bound
  private proposedValidityExistReaction() {
    // When we receive proposed validity, we have to set it,
    // and thus call the endpoint again. After 2nd call,
    // proposedValidityExist will always be false.
    if (!this.proposedValidityExist) {
      // If the user changes validSince while the proposed validity
      // alert exists, it should remove the alert.
      this.proposedValidityFlag = false;
    }
    if (this.proposedValidityExist) {
      this.proposedValidityExist = false;
    }
  }

  @action.bound
  private async proposedValidityReaction() {
    if (
      !this.chargeOrderChargeItem.chargeTypeId ||
      !this.chargeOrderChargeItem.validSince ||
      !this.chargeOrderService.chargeOrderItem.licensePlate
    ) {
      return;
    }
    try {
      this.checkingProposedValidity = true;
      this.validSinceString = dateToIsoDate(this.chargeOrderChargeItem.validSince);
      this.updateChargeOrderItem();
      await this.debouncedCheckProposedValidity();
    } catch (e) {
      // Silence catch of background process
    }
  }

  @action.bound
  @repeatIfFailed
  private async checkProposedValidity() {
    this.checkingProposedValidity = true;
    const { countryId, licensePlate, priceListItemId } = this.chargeOrderService.chargeOrderItem;
    if (countryId && licensePlate && priceListItemId) {
      const isNow = this.validSinceString === dateToIsoString(this.currentServerTime);
      if (!this.validSinceString || this.validSinceString < dateToIsoString(this.currentServerTime)) {
        return;
      }
      const response = await this.lastResolveGetProposedValidity(
        countryId,
        licensePlate,
        priceListItemId,
        isNow,
        undefined,
        this.validSinceString
      );

      if (response instanceof ChargeValidityInstructionsResponse) {
        await this.setProposedValidity(response);
      } else {
        throw new Error("Proposed validity check failed");
      }
    }
    this.checkingProposedValidity = false;
  }

  @action.bound
  private async setProposedValidity(validChargesResponse: ChargeValidityInstructionsResponse) {
    const { overlapTypes, overlappingCharges } = validChargesResponse;
    this.chargeOrderChargeItem._validUntil = this.chargeOrderService.chargeOrderItem.validity._validUntilDate =
      validChargesResponse.validUntil;
    this.validSince = validChargesResponse.validSince;
    this.validSinceString = dateToIsoDate(validChargesResponse.validSince);
    // The validity change by the BE can only happen once. If trying for the second time, the validity cannot be changed anymore by the system.
    if (this.proposedValiditySetAlready) {
      return;
    }

    this.showExemptionOverlap = overlapTypes?.includes(ChargeOverlapType.ExemptionOverlap);
    this.showChargeOverlap = overlapTypes?.includes(ChargeOverlapType.ChargeOverlap);
    this.overlappingCharges = overlappingCharges ? overlappingCharges : [];
    if (validChargesResponse.proposedValidity) {
      const hasImmediateValidity = isSameOrBeforePragueDay(
        validChargesResponse.proposedValidity?.validSince,
        this.currentServerTime
      );
      this.chargeOrderChargeItem.validSince = new Date(
        zonedCZTimeToUtc(dateToIsoDate(validChargesResponse.proposedValidity?.validSince))
      );
      this.chargeOrderChargeItem._validUntil = new Date(
        endOfCzechDay(zonedCZTimeToUtc(dateToIsoDate(validChargesResponse.proposedValidity?.validUntil)))
      );
      this.validSinceString = dateToIsoDate(validChargesResponse.proposedValidity?.validSince);
      if (hasImmediateValidity) {
        this.chargeOrderChargeItem.validSince = undefined;
        this.chargeOrderChargeItem._hasImmediateValidity = hasImmediateValidity;
      }
      this.validSince = this.chargeOrderChargeItem.validSince;
      this.chargeOrderService.persistChargeOrder();
      this.proposedValidityFlag = true;
      this.proposedValidityExist = true;
      this.proposedValiditySetAlready = true;
    }
  }

  @action.bound
  public async onContinue() {
    if (!this.validate()) {
      return;
    }
    const chargeOrderItem = this.chargeOrderService.chargeOrderItem;
    chargeOrderItem.validity.validSinceDate = this.chargeOrderChargeItem.validSince = this.validSince;

    this.updateChargeOrderItem();
    this.chargeOrderService.persistStoredChargeOrder(this.chargeOrderService.chargeOrder);
    this.router.navigate(ContactViewModel);
  }

  @action.bound
  public onBack() {
    this.router.navigate(FuelTypeViewModel);
  }

  @action.bound
  private validateAll() {
    const isValid = validate(this.chargeOrderChargeItem);
    if (!isValid) {
      scrollToFirstError(this.formElement);
    }

    return isValid;
  }

  @action.bound
  private updateDateRangeValidation() {
    this.manualValidator.removeError("validSince");

    if (!this.timeService.currentServerTime) {
      return;
    }

    const validSince = this.chargeOrderChargeItem.validSince;
    if (!validSince) {
      return;
    }

    const serverTime = this.timeService.currentServerTime;
    const dateFormat = "yyyy-MM-dd";
    const minDate = zonedCZTimeToUtc(format(serverTime, dateFormat));
    const maxDate = zonedCZTimeToUtc(
      format(
        maxValidSince(
          this.timeService.currentServerTime,
          this.validSinceSelectViewModel.postponedValidityRule?.postponedValidityMaxDays
        ),
        dateFormat
      )
    );

    const validSinceDate = zonedCZTimeToUtc(format(new Date(validSince), dateFormat));
    if (validSinceDate < minDate) {
      const message = this.localizationService.translateAttribute("validSince", "min");
      this.manualValidator.addError("validSince", message);
      setVisitedProperty(this.chargeOrderChargeItem, "validSince");
    } else if (validSinceDate > maxDate) {
      const message = this.localizationService
        .translateAttribute("validSince", "max")
        .replace("%amount%", `${this.validSinceSelectViewModel.postponedValidityRule?.postponedValidityMaxDays}`);
      this.manualValidator.addError("validSince", message);
      setVisitedProperty(this.chargeOrderChargeItem, "validSince");
    }
  }

  @action.bound
  private removeDateRangeValidationErrors() {
    this.manualValidator.removeError("validSince");
  }

  @bound
  public validate() {
    this.updateDateRangeValidation();

    const objectsForValidation: any[] = [this.chargeOrderChargeItem];
    return validateAll(objectsForValidation);
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrderChargeItem: ChargeOrderChargeItem) =>
      new ChargeTypeViewModel(
        chargeOrderChargeItem,
        container.get(LocalizationService),
        container.get(ChargeOrderService),
        container.get(ChargeTypesService),
        container.get(TimeService),
        container.get(EnumService),
        container.get(AppRouter),
        container.get(VehicleRepository),
        container.get(ChargeTypeSelectViewModel.Factory),
        container.get(ValiditySelectViewModel.Factory),
        container.get(EventBus)
      );
  }
}
